import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircle2,
  AlertTriangle,
  RefreshCcw,
  Edit3,
  Hourglass,
  FolderOpen,
  Loader2,
  Database,
  ChevronDown,
  Check,
  Layers,
  HardDrive,
  Activity,
  Zap,
  ShieldCheck,
  Gauge,
  Boxes,
  X,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';
import MultiSelectApiDropdownWithAll from '../../../components/MultiSelectApiDropdownWithAll';

interface DatabasesViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

interface CustomSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: CustomSelectOption[];
  icon?: React.ReactNode;
  minWidth?: string;
}

const fmtUptime = (sec: number | null | undefined) => {
  if (sec == null || sec <= 0) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${sec}s`;
};

const fmtSize = (mb: number | null | undefined) => {
  if (mb == null || mb <= 0) return '—';
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${(mb * 1024).toFixed(0)} KB`;
};

const fmtCompact = (n: number | null | undefined) => {
  if (n == null) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return typeof n === 'number' ? n.toLocaleString() : String(n);
};

// const fmtBytes = (bytes: number | null | undefined) => {
//   if (bytes == null || bytes <= 0) return '—';
//   if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
//   if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
//   if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
//   return `${bytes} B`;
// };

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  icon,
  minWidth = '130px',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateCoords = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dWidth = Math.min(Math.max(rect.width, 140), window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.left + window.scrollX, window.innerWidth - dWidth - 8));
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left,
        width: dWidth,
      });
    }
  }, []);

  const toggleOpen = () => {
    if (!isOpen) {
      updateCoords();
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleScrollOrResize = () => {
      if (isOpen) {
        updateCoords();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleScrollOrResize);
      window.addEventListener('scroll', handleScrollOrResize, true);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [isOpen, updateCoords]);

  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        style={{ minWidth }}
        className="flex items-center justify-between gap-1.5 bg-gray-50 dark:bg-gray-900/90 hover:bg-gray-100 dark:hover:bg-gray-800/80 border border-gray-200 dark:border-gray-700/80 rounded-lg px-2 py-1 text-[10px] text-gray-800 dark:text-gray-200 font-semibold focus:outline-none focus:border-orange-500 transition-all cursor-pointer shadow-sm h-7"
      >
        <div className="flex items-center gap-1 truncate min-w-0">
          {selectedOption?.icon || icon}
          <span className="truncate">{selectedOption?.label || value}</span>
        </div>
        <ChevronDown className={`w-3 h-3 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
              zIndex: 99999,
            }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl overflow-hidden py-1 max-h-60 overflow-y-auto custom-scroll animate-in fade-in slide-in-from-top-1 duration-150"
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-2.5 py-1 text-[11px] font-semibold flex items-center justify-between gap-2 cursor-pointer transition-colors ${isSelected
                    ? 'bg-orange-50 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400 font-bold'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                  <div className="flex items-center gap-1.5 truncate min-w-0">
                    {opt.icon}
                    <span className="truncate">{opt.label}</span>
                  </div>
                  {isSelected && <Check className="w-3 h-3 text-orange-500 dark:text-orange-400 shrink-0" />}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700 dark:text-gray-200 text-[9px] truncate max-w-[130px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[130px] z-50">
        {label !== undefined && label !== null && label !== "" && (
          <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate">
            {label}
          </div>
        )}
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color || entry.stroke || entry.fill || "#f97316" }}
              />
              {entry.name || entry.dataKey}
            </span>
            <span className="font-bold text-gray-900 dark:text-white font-mono">
              {typeof entry.value === 'number' ? entry.value.toLocaleString() : (entry.value ?? '-')}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const DatabasesView: React.FC<DatabasesViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  dbType,
}) => {
  const [loading, setLoading] = useState(true);
  const [metricsData, setMetricsData] = useState<any>(null);
  const [commitsData, setCommitsData] = useState<any>(null);
  const [connectionsData, setConnectionsData] = useState<any>(null);
  const [rowsData, setRowsData] = useState<any>(null);
  const [txnsData, setTxnsData] = useState<any>(null);
  const [queryThroughputData, setQueryThroughputData] = useState<any>(null);
  const [queryDurationData, setQueryDurationData] = useState<any>(null);
  const [selectedDatabases, setSelectedDatabases] = useState<any[]>([{ id: '*', name: 'Select all' }]);

  // Per-chart Database Filter States
  const [selectedCommitsDb, setSelectedCommitsDb] = useState<string>('ALL');
  const [selectedRollbacksDb, setSelectedRollbacksDb] = useState<string>('ALL');
  const [selectedConnsDb, setSelectedConnsDb] = useState<string>('ALL');
  const [selectedRowsDb, setSelectedRowsDb] = useState<string>('ALL');
  const [selectedMaxTxnDb, setSelectedMaxTxnDb] = useState<string>('ALL');
  const [selectedOpenTxnDb, setSelectedOpenTxnDb] = useState<string>('ALL');

  // Legend Hover States
  const [hoveredCommits, setHoveredCommits] = useState<string | null>(null);
  const [hoveredRollbacks, setHoveredRollbacks] = useState<string | null>(null);
  const [hoveredConns, setHoveredConns] = useState<string | null>(null);
  const [hoveredRows, setHoveredRows] = useState<string | null>(null);
  const [hoveredMaxTxn, setHoveredMaxTxn] = useState<string | null>(null);
  const [hoveredOpenTxn, setHoveredOpenTxn] = useState<string | null>(null);
  const [hoveredThroughput, setHoveredThroughput] = useState<string | null>(null);
  const [hoveredDuration, setHoveredDuration] = useState<string | null>(null);

  const availableDatabases = useMemo(() => {
    const set = new Set<string>();
    (commitsData?.databases || []).forEach((db: string) => set.add(db));
    (connectionsData?.databases || []).forEach((db: string) => set.add(db));
    (rowsData?.databases || []).forEach((db: string) => set.add(db));
    (txnsData?.databases || []).forEach((db: string) => set.add(db));
    return Array.from(set);
  }, [commitsData, connectionsData, rowsData, txnsData]);

  const ChartDbDropdown = ({
    value,
    onChange,
    options,
  }: {
    value: string;
    onChange: (val: string) => void;
    options: string[];
  }) => {
    const dbList = options && options.length > 0 ? options : availableDatabases;
    if (dbList.length === 0) return null;

    const selectOptions: CustomSelectOption[] = [
      { value: 'ALL', label: 'All Databases' },
      ...dbList.map((db) => ({ value: db, label: db })),
    ];

    return <CustomSelect value={value} onChange={onChange} options={selectOptions} minWidth="130px" />;
  };

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60');

    const baseParams: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) baseParams.client_id = clientId;

    const dbNames = selectedDatabases
      .map((item) => (typeof item === 'string' ? item : item?.id || item?.name || item?.database))
      .filter((name) => name && name !== '*');

    if (dbNames.length > 0) {
      baseParams.database = dbNames.join(',');
    }

    const safeGet = async (url: string, params: any) => {
      try {
        const res = await baseapi.get(url, { params });
        return res.data?.data || res.data;
      } catch (e) {
        return null;
      }
    };

    try {
      const [mRes, cRes, connRes, rRes, tRes, qtRes, qdRes] = await Promise.all([
        safeGet('/db_monitor_dashboard/metrics', { cluster_id: activeCluster, ...(clientId ? { client_id: clientId } : {}) }),
        safeGet('/db_monitor_dashboard/graph/per_db/commits', baseParams),
        safeGet('/db_monitor_dashboard/graph/per_db/connections', baseParams),
        safeGet('/db_monitor_dashboard/graph/per_db/rows', baseParams),
        safeGet('/db_monitor_dashboard/graph/per_db/transactions', baseParams),
        safeGet('/db_monitor_dashboard/graph/query_throughput', baseParams),
        safeGet('/db_monitor_dashboard/graph/query_duration', baseParams),
      ]);

      setMetricsData(mRes);
      setCommitsData(cRes);
      setConnectionsData(connRes);
      setRowsData(rRes);
      setTxnsData(tRes);
      setQueryThroughputData(qtRes);
      setQueryDurationData(qdRes);
    } catch (err) {
      console.warn('DatabasesView fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId, selectedDatabases]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const effectiveDbType = (dbType || metricsData?.db_type || '').toLowerCase();
  const isMongo = ['mongodb', 'mongo'].includes(effectiveDbType) || effectiveDbType.includes('mongo') || (activeCluster || '').toLowerCase().includes('mongo');
  const isMySQL = !isMongo && (['mysql', 'mariadb'].includes(effectiveDbType) || effectiveDbType.includes('mysql') || (activeCluster || '').toLowerCase().includes('mysql'));
  const isMSSQL = ['mssql', 'sqlserver', 'sql server', 'sql_server'].includes(effectiveDbType) || effectiveDbType.includes('mssql') || (activeCluster || '').toLowerCase().includes('mssql');
  const isMariaDB = effectiveDbType.includes('mariadb') || (metricsData?.db_type || '').toLowerCase().includes('mariadb') || (activeCluster || '').toLowerCase().includes('mariadb') || !!metricsData?.mariadb_galera || !!metricsData?.mariadb_query_cache;

  const formatTimeLabels = (labels: string[]) => {
    if (!labels || labels.length === 0) return [];
    const minutes = parseInt(timeRange || '60');
    const seen: { [key: string]: number } = {};
    return labels.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const formatSeries = (labels: string[], databases: string[], seriesMap: any, metricKey: string) => {
    if (!labels || labels.length === 0 || !databases) return [];
    const timeLabels = formatTimeLabels(labels);
    return labels.map((_, idx) => {
      const point: any = { time: timeLabels[idx] };
      databases.forEach((db) => {
        const val = seriesMap?.[`${db}:${metricKey}`]?.[idx];
        point[db] = val !== undefined && val !== null ? Number(val.toFixed(2)) : 0;
      });
      return point;
    });
  };

  const commitsFormatted = formatSeries(commitsData?.labels || [], commitsData?.databases || [], commitsData?.series || {}, 'commits');
  const rollbacksFormatted = formatSeries(commitsData?.labels || [], commitsData?.databases || [], commitsData?.series || {}, 'rollbacks');
  const connectionsFormatted = formatSeries(connectionsData?.labels || [], connectionsData?.databases || [], connectionsData?.series || {}, 'connections');
  const maxTxnFormatted = formatSeries(txnsData?.labels || [], txnsData?.databases || [], txnsData?.series || {}, 'max_txn_time_sec');
  const openTxnFormatted = formatSeries(txnsData?.labels || [], txnsData?.databases || [], txnsData?.series || {}, 'open_txns');

  const throughputTimeLabels = formatTimeLabels(queryThroughputData?.labels || []);
  const throughputFormatted = (queryThroughputData?.labels || []).map((_: string, idx: number) => {
    const point: any = { time: throughputTimeLabels[idx] };
    (queryThroughputData?.query_types || []).forEach((qt: string) => {
      point[qt.toUpperCase()] = Number((queryThroughputData?.series?.[qt]?.[idx] || 0).toFixed(2));
    });
    return point;
  });

  const durationTimeLabels = formatTimeLabels(queryDurationData?.labels || []);
  const durationFormatted = (queryDurationData?.labels || []).map((_: string, idx: number) => {
    const point: any = { time: durationTimeLabels[idx] };
    (queryDurationData?.query_types || []).forEach((qt: string) => {
      point[qt.toUpperCase()] = Number((queryDurationData?.series?.[qt]?.[idx] || 0).toFixed(2));
    });
    return point;
  });

  const rowsTimeLabels = formatTimeLabels(rowsData?.labels || []);
  const getRowsFormatted = (selectedDb: string) => {
    return (rowsData?.labels || []).map((_: string, idx: number) => {
      const point: any = { time: rowsTimeLabels[idx] };
      ['rows_inserted', 'rows_updated', 'rows_deleted'].forEach((op) => {
        let sum = 0;
        const dbsToInclude = selectedDb === 'ALL'
          ? (rowsData?.databases || [])
          : (rowsData?.databases || []).filter((db: string) => db === selectedDb);

        dbsToInclude.forEach((db: string) => {
          const val = (rowsData?.series?.[`${db}:${op}`] || [])[idx];
          if (val !== undefined && val !== null) {
            sum += Number(val);
          }
        });
        let name = 'Inserted';
        if (op === 'rows_updated') name = 'Updated';
        if (op === 'rows_deleted') name = 'Deleted';
        point[name] = Number(sum.toFixed(2));
      });
      return point;
    });
  };
  const rowsFormatted = getRowsFormatted(selectedRowsDb);

  const colors = ['#3b82f6', '#10b981', '#a855f7', '#f59e0b', '#ef4444', '#06b6d4'];
  const hasData = (arr: any[]) => arr && arr.length > 0;

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  const getDot = (_hovered?: any, _key?: any, _color?: any, _defaultR?: any) => false;

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[400px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  // Calculate buffer pool percentages
  const bpTotal = metricsData?.innodb_buffer_pool_pages_total || 0;
  const bpData = metricsData?.innodb_buffer_pool_pages_data || 0;
  const bpFree = metricsData?.innodb_buffer_pool_pages_free || 0;
  const bpDataPct = bpTotal > 0 ? ((bpData / bpTotal) * 100).toFixed(1) : '0';
  const bpFreePct = bpTotal > 0 ? ((bpFree / bpTotal) * 100).toFixed(1) : '0';

  return (
    <div className="space-y-4 relative">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-md">
            <Database className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span>Database Performance</span>
              {isMySQL && (
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-md bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30">
                  {effectiveDbType} {metricsData?.version ? `v${metricsData.version.split('-')[0]}` : ''}
                </span>
              )}
            </h2>
            {isMySQL && metricsData?.hostname && (
              <p className="text-[11px] text-gray-500 dark:text-gray-200">
                Host: <span className="font-mono text-gray-700 dark:text-gray-300">{metricsData.hostname}</span> ({metricsData.ip || metricsData.host}:{metricsData.port || 3306}) • Uptime: <span className="text-gray-700 dark:text-gray-300 font-mono">{fmtUptime(metricsData.uptime_sec)}</span>
              </p>
            )}
          </div>
        </div>

        {/* Database Multi-Select Dropdown */}
        <div className="w-full sm:w-72">
          <MultiSelectApiDropdownWithAll
            placeholder="Select Databases"
            apiEndpoint="/db_monitor_dashboard/databases"
            additionalParams={{
              cluster_id: activeCluster,
              ...(clientId ? { client_id: clientId } : {}),
            }}
            displayProperty="name"
            idProperty="id"
            searchFields={['name']}
            value={selectedDatabases}
            onChange={(items) => setSelectedDatabases(items)}
            selectAll={true}
            buttonHeight={40}
          />
        </div>
      </div>

      {/* MySQL Specific Performance Telemetry Panels */}
      {isMySQL && metricsData && (
        <div className="space-y-3.5">
          {/* 6 Key MySQL Stat Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {/* 1. Cluster Health & Role */}
            <div className="bg-emerald-50/60 dark:bg-emerald-600/10 border border-emerald-200 dark:border-emerald-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
              <div className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-200 font-semibold mb-1">
                <span>Cluster Health</span>
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse" />
                <span className="text-xs font-bold uppercase text-emerald-700 dark:text-emerald-400 tracking-wider">
                  {metricsData.cluster_health || 'HEALTHY'}
                </span>
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-200 mt-1 capitalize">
                Role: <span className="text-gray-800 dark:text-gray-200 font-semibold">{metricsData.role || metricsData.actual_role || 'Standalone'}</span>
              </div>
            </div>

            {/* 2. QPS & TPS */}
            <div className="bg-blue-50/60 dark:bg-blue-600/10 border border-blue-200 dark:border-blue-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
              <div className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-200 font-semibold mb-1">
                <span>Throughput</span>
                <Zap className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-base font-bold font-mono text-blue-700 dark:text-blue-300">
                {metricsData.qps != null ? Number(metricsData.qps).toFixed(2) : '0.00'}{' '}
                <span className="text-[10px] text-gray-500 dark:text-gray-200 font-normal">QPS</span>
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-200 mt-0.5">
                TPS: <span className="text-gray-700 dark:text-gray-200 font-mono">{metricsData.tps != null ? Number(metricsData.tps).toFixed(2) : '0'}</span> • Slow: <span className={`font-mono ${metricsData.slow_queries_total > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-200'}`}>{metricsData.slow_queries_total ?? 0}</span>
              </div>
            </div>

            {/* 3. Buffer Pool Hit Rate */}
            <div className="bg-purple-50/60 dark:bg-purple-600/10 border border-purple-200 dark:border-purple-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
              <div className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-200 font-semibold mb-1">
                <span>Buffer Pool Hit</span>
                <CheckCircle2 className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="text-base font-bold font-mono text-purple-700 dark:text-purple-300">
                {metricsData.cache_hit_pct != null ? `${Number(metricsData.cache_hit_pct).toFixed(2)}%` : '—'}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-200 mt-0.5 truncate">
                Reads: <span className="text-gray-700 dark:text-gray-200 font-mono">{metricsData.buffer_pool_read_requests_per_s != null ? `${Number(metricsData.buffer_pool_read_requests_per_s).toFixed(1)}/s` : '—'}</span>
              </div>
            </div>

            {/* 4. Active Connections */}
            <div className="bg-amber-50/60 dark:bg-amber-600/10 border border-amber-200 dark:border-amber-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
              <div className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-200 font-semibold mb-1">
                <span>Connections</span>
                <Activity className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              </div>
              <div
                className="text-sm sm:text-base font-bold font-mono text-amber-700 dark:text-amber-300 flex items-baseline flex-wrap gap-x-1"
                title={`Active: ${metricsData.connections_active ?? 0} | Idle: ${metricsData.connections_idle ?? 0} | Max: ${metricsData.connections_max ?? 151}`}
              >
                <span>{metricsData.connections_active ?? metricsData.connections_total ?? 0}</span>
                <span className="text-[10px] text-gray-500 dark:text-gray-200 font-normal">active</span>
                <span className="text-gray-300 dark:text-gray-600 font-normal">•</span>
                <span>{metricsData.connections_idle ?? 0}</span>
                <span className="text-[10px] text-gray-500 dark:text-gray-200 font-normal">idle</span>
                <span className="text-xs text-gray-500 dark:text-gray-200 font-normal">/ {metricsData.connections_max ?? 151}</span>
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-200 mt-0.5 truncate">
                Util: <span className="text-gray-700 dark:text-gray-200 font-mono">{metricsData.connections_util_pct != null ? `${Number(metricsData.connections_util_pct).toFixed(1)}%` : '0%'}</span> • Max: <span className="text-gray-700 dark:text-gray-200 font-mono">{metricsData.mysql_max_used_connections ?? '—'}</span>
              </div>
            </div>

            {/* 5. InnoDB Rows / Sec */}
            <div className="bg-cyan-50/60 dark:bg-cyan-600/10 border border-cyan-200 dark:border-cyan-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
              <div className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-200 font-semibold mb-1">
                <span>InnoDB Rows/s</span>
                <Edit3 className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div className="text-base font-bold font-mono text-cyan-700 dark:text-cyan-300">
                {metricsData.innodb_rows_read_per_sec != null ? Number(metricsData.innodb_rows_read_per_sec).toFixed(2) : '0.00'}{' '}
                <span className="text-[10px] text-gray-500 dark:text-gray-200 font-normal">read</span>
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-200 mt-0.5 truncate">
                Insert: <span className="text-gray-700 dark:text-gray-200 font-mono">{metricsData.innodb_rows_inserted_per_sec != null ? `${Number(metricsData.innodb_rows_inserted_per_sec).toFixed(2)}/s` : '0/s'}</span>
              </div>
            </div>

            {/* 6. Database Storage */}
            <div className="bg-rose-50/60 dark:bg-rose-600/10 border border-rose-200 dark:border-rose-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
              <div className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-200 font-semibold mb-1">
                <span>Database Size</span>
                <HardDrive className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
              </div>
              <div className="text-base font-bold font-mono text-rose-700 dark:text-rose-300">
                {fmtSize(metricsData.database_size_mb)}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-200 mt-0.5 truncate">
                <div>
                  User databases: <span className="text-gray-700 dark:text-gray-200 font-semibold">{metricsData.database_count ?? 1}</span>
                </div>
                <div>
                  Disk Free: <span className="text-gray-700 dark:text-gray-200 font-mono">{fmtSize(metricsData.disk_free_mb)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* MySQL Deep Telemetry Cards (Buffer Pool, Query Efficiency, I/O & Storage) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
            {/* Card 1: InnoDB Buffer Pool & Pages Breakdown */}
            <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-lg space-y-3">
              <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2">
                <div className="flex items-center gap-2">
                  <Boxes className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">InnoDB Buffer Pool</h3>
                </div>
                <span className="text-[11px] font-mono text-purple-600 dark:text-purple-400 font-semibold">
                  {fmtSize(metricsData.memory_buffer_pool_mb)}
                </span>
              </div>

              {/* Progress Bar of Pages */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-[11px] text-gray-700 dark:text-gray-300">
                  <span>Page Utilization</span>
                  <span className="font-mono text-gray-500 dark:text-gray-200">{fmtCompact(bpData)} / {fmtCompact(bpTotal)} pages</span>
                </div>
                <div className="w-full bg-gray-100 dark:bg-gray-700/60 rounded-full h-2 overflow-hidden flex">
                  <div style={{ width: `${bpDataPct}%` }} className="bg-purple-500 h-full rounded-l" title={`Data Pages: ${bpDataPct}%`} />
                  <div style={{ width: `${bpFreePct}%` }} className="bg-emerald-500 h-full rounded-r" title={`Free Pages: ${bpFreePct}%`} />
                </div>
                <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-200">
                  <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-purple-500" /> Data ({bpDataPct}%)</span>
                  <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Free ({bpFreePct}%)</span>
                </div>
              </div>

              {/* Stats List */}
              <div className="grid grid-cols-2 gap-2 pt-1 border-t border-gray-100 dark:border-gray-700/30 text-xs">
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">Pages Flushed</div>
                  <div className="font-mono text-gray-900 dark:text-white font-bold">{fmtCompact(metricsData.innodb_buffer_pool_pages_flushed)}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">Read Ahead / Evicted</div>
                  <div className="font-mono text-gray-900 dark:text-white font-bold">{fmtCompact(metricsData.innodb_buffer_pool_read_ahead)} / {fmtCompact(metricsData.innodb_buffer_pool_read_ahead_evicted)}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">Memory Used / Peak</div>
                  <div className="font-mono text-gray-900 dark:text-white font-bold">{fmtSize(metricsData.memory_used_mb)} / {fmtSize(metricsData.memory_peak_mb)}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">Change Buffer Pages</div>
                  <div className="font-mono text-gray-900 dark:text-white font-bold">{fmtCompact(metricsData.innodb_change_buffer_size_pages)}</div>
                </div>
              </div>
            </div>

            {/* Card 2: Query Execution & Lock Performance */}
            <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-lg space-y-3">
              <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2">
                <div className="flex items-center gap-2">
                  <Gauge className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                  <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">Query & Lock Efficiency</h3>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${metricsData.deadlock_count > 0 ? 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30' : 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30'}`}>
                  {metricsData.deadlock_count > 0 ? `${metricsData.deadlock_count} Deadlocks` : '0 Deadlocks'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">Full Table Scans/s</div>
                  <div className="font-mono text-gray-900 dark:text-white font-bold">{metricsData.full_table_scans_per_sec != null ? Number(metricsData.full_table_scans_per_sec).toFixed(2) : '0'}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">Joins Without Index</div>
                  <div className={`font-mono font-bold ${metricsData.full_joins_without_index > 1000 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
                    {fmtCompact(metricsData.full_joins_without_index)}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">Temp Tables to Disk %</div>
                  <div className="font-mono text-gray-900 dark:text-white font-bold">{metricsData.temp_tables_to_disk_pct != null ? `${Number(metricsData.temp_tables_to_disk_pct).toFixed(1)}%` : '0%'}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">Lock Waits / Backlog</div>
                  <div className="font-mono text-gray-900 dark:text-white font-bold">{metricsData.lock_waits ?? 0} / {metricsData.mysql_purge_backlog_len ?? 0}</div>
                </div>
              </div>

              {/* Capabilities */}
              <div className="pt-1 border-t border-gray-100 dark:border-gray-700/30 flex items-center justify-between text-[11px]">
                <span className="text-gray-500 dark:text-gray-200">Event Scheduler:</span>
                <span className={`font-semibold ${metricsData.jobs_capability?.status === 'supported_enabled' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'} flex items-center gap-1`}>
                  {metricsData.jobs_capability?.status === 'supported_enabled' ? (
                    <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <X className="w-3 h-3 text-red-600 dark:text-red-400" />
                  )}
                  {metricsData.jobs_capability?.status === 'supported_enabled' ? 'Enabled' : metricsData.jobs_capability?.status ? 'Disabled' : '-'}
                </span>
              </div>
            </div>

            {/* Card 3: InnoDB Log & File I/O Engine */}
            <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-lg space-y-3">
              <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2">
                <div className="flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                  <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">InnoDB File I/O & Logs</h3>
                </div>
                <span className="text-[11px] font-mono text-cyan-600 dark:text-cyan-400">
                  {metricsData.mysql_file_io_active_files
                    && `${metricsData.mysql_file_io_active_files} Active Files`}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">Read / Write Latency</div>
                  <div className="font-mono text-gray-900 dark:text-white font-bold">
                    {metricsData.mysql_file_io_read_latency_ms ?? '-'}ms / {metricsData.mysql_file_io_write_latency_ms ?? '-'}ms
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">Write Throughput</div>
                  <div className="font-mono text-gray-900 dark:text-white font-bold">
                    {metricsData.mysql_file_io_write_mb_per_sec ?? '-'} MB/s ({metricsData.mysql_file_io_write_iops ?? '-'} IOPS)
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">InnoDB Log Writes</div>
                  <div className="font-mono text-gray-900 dark:text-white font-bold">{fmtCompact(metricsData.innodb_log_writes)}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                  <div className="text-[10px] text-gray-500 dark:text-gray-200">OS Log Fsyncs</div>
                  <div className="font-mono text-gray-900 dark:text-white font-bold">{fmtCompact(metricsData.innodb_os_log_fsyncs)}</div>
                </div>
              </div>

              {/* Data dir & Disk usage */}
              <div className="pt-1 border-t border-gray-100 dark:border-gray-700/30 flex items-center justify-between text-[11px] truncate">
                <span className="text-gray-500 dark:text-gray-200 truncate">Data Dir:</span>
                <span className="font-mono text-gray-700 dark:text-gray-300 text-[10px] truncate max-w-[170px]" title={metricsData.disk_data_dir}>
                  {metricsData.disk_data_dir || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* MariaDB Specific Capabilities & Clustering */}
          {(isMariaDB || metricsData.mariadb_galera || metricsData.mariadb_query_cache || metricsData.thread_pool_capability) && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Boxes className="w-4 h-4 text-orange-500" />
                <h3 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                  MariaDB Capabilities &amp; Cluster Architecture
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                {/* 1. Thread Pool Capability */}
                <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-lg space-y-3">
                  <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2">
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                        Thread Pool
                      </h3>
                    </div>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 ${metricsData.thread_pool_capability?.status === 'supported_enabled'
                        ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30'
                        : metricsData.thread_pool_capability?.status === 'supported_disabled'
                          ? 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                    >
                      {metricsData.thread_pool_capability?.status === 'supported_enabled' ? (
                        <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <X className="w-3 h-3 text-rose-600 dark:text-rose-400" />
                      )}
                      {metricsData.thread_pool_capability?.status === 'supported_enabled'
                        ? 'Enabled'
                        : metricsData.thread_pool_capability?.status === 'supported_disabled'
                          ? 'Disabled'
                          : metricsData.thread_pool_capability?.status || 'N/A'}
                    </span>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                      <span className="text-[10px] text-gray-500 dark:text-gray-200">Mechanism</span>
                      <span className="font-mono font-semibold text-gray-900 dark:text-white uppercase text-[11px]">
                        {metricsData.thread_pool_capability?.mechanism?.replace('_', ' ') || 'thread_pool'}
                      </span>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2.5 border border-gray-200 dark:border-gray-800 space-y-1">
                      <div className="text-[10px] text-gray-500 dark:text-gray-200">Configuration Detail</div>
                      <div className="font-mono text-[11px] text-gray-800 dark:text-gray-200 break-all font-semibold">
                        {metricsData.thread_pool_capability?.detail || '—'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Query Cache */}
                <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-lg space-y-3">
                  <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                      <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                        Query Cache
                      </h3>
                    </div>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 ${metricsData.mariadb_query_cache?.capability?.status === 'supported_enabled'
                        ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30'
                        : metricsData.mariadb_query_cache?.capability?.status === 'supported_disabled'
                          ? 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                    >
                      {metricsData.mariadb_query_cache?.capability?.status === 'supported_enabled' ? (
                        <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <X className="w-3 h-3 text-rose-600 dark:text-rose-400" />
                      )}
                      {metricsData.mariadb_query_cache?.capability?.status === 'supported_enabled'
                        ? 'Enabled'
                        : metricsData.mariadb_query_cache?.capability?.status === 'supported_disabled'
                          ? 'Disabled'
                          : metricsData.mariadb_query_cache?.capability?.status || 'N/A'}
                    </span>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                        <div className="text-[10px] text-gray-500 dark:text-gray-200">Allocated Size</div>
                        <div className="font-mono text-gray-900 dark:text-white font-bold text-xs">
                          {metricsData.mariadb_query_cache?.size_mb != null
                            ? fmtSize(metricsData.mariadb_query_cache.size_mb)
                            : '—'}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                        <div className="text-[10px] text-gray-500 dark:text-gray-200">Mechanism</div>
                        <div className="font-mono text-gray-900 dark:text-white font-bold text-xs capitalize">
                          {metricsData.mariadb_query_cache?.capability?.mechanism?.replace('_', ' ') || 'query_cache'}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2.5 border border-gray-200 dark:border-gray-800 space-y-1">
                      <div className="text-[10px] text-gray-500 dark:text-gray-200">Configuration Detail</div>
                      <div className="font-mono text-[11px] text-gray-800 dark:text-gray-200 break-all font-semibold">
                        {metricsData.mariadb_query_cache?.capability?.detail || '—'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. Galera Cluster */}
                <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-lg space-y-3">
                  <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2">
                    <div className="flex items-center gap-2">
                      <Boxes className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                        Galera Cluster
                      </h3>
                    </div>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 ${metricsData.mariadb_galera?.connected
                        ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30'
                        : 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30'
                        }`}
                    >
                      {metricsData.mariadb_galera?.connected ? (
                        <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <X className="w-3 h-3 text-rose-600 dark:text-rose-400" />
                      )}
                      {metricsData.mariadb_galera?.cluster_status || (metricsData.mariadb_galera?.connected ? 'Connected' : 'Disconnected')}
                    </span>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800 flex items-center justify-between">
                        <div className="text-[10px] text-gray-500 dark:text-gray-200">Connected</div>
                        <div className="flex items-center gap-1 font-semibold text-xs">
                          {metricsData.mariadb_galera?.connected ? (
                            <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Yes</span>
                          ) : (
                            <span className="text-rose-600 dark:text-rose-400 flex items-center gap-1"><X className="w-3 h-3" /> No</span>
                          )}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2 border border-gray-200 dark:border-gray-800 flex items-center justify-between">
                        <div className="text-[10px] text-gray-500 dark:text-gray-200">Node Ready</div>
                        <div className="flex items-center gap-1 font-semibold text-xs">
                          {metricsData.mariadb_galera?.ready ? (
                            <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Yes</span>
                          ) : (
                            <span className="text-rose-600 dark:text-rose-400 flex items-center gap-1"><X className="w-3 h-3" /> No</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg p-2.5 border border-gray-200 dark:border-gray-800 space-y-1">
                      <div className="text-[10px] text-gray-500 dark:text-gray-200">Cluster Status / Detail</div>
                      <div className="font-mono text-[11px] text-gray-800 dark:text-gray-200 break-words font-medium">
                        {metricsData.mariadb_galera?.capability?.detail || '—'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!isMariaDB && (
        <div>
          {/* Row 1: 2 Graphs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            {/* Graph 1: Commits */}
            <div className="">
              <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
                <div className="flex items-center gap-2 truncate">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-green-400 shrink-0" />
                  <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider truncate">Commits per Database</h4>
                </div>
                <ChartDbDropdown value={selectedCommitsDb} onChange={setSelectedCommitsDb} options={commitsData?.databases} />
              </div>
              <div className="h-40 w-full flex items-center justify-center">
                {hasData(commitsFormatted) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={commitsFormatted}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                      <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                      <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                      <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                      <Legend content={<ScrollableLegend hoveredSeries={hoveredCommits} onHoverSeries={setHoveredCommits} />} />
                      {(() => {
                        const allDbKeys = Object.keys(commitsFormatted[0] || {}).filter((k) => k !== 'time');
                        return allDbKeys
                          .filter((k) => selectedCommitsDb === 'ALL' || k === selectedCommitsDb)
                          .map((dbKey, i) => {
                            const origIndex = allDbKeys.indexOf(dbKey);
                            const colorIndex = origIndex >= 0 ? origIndex : i;
                            const color = colors[colorIndex % colors.length];
                            return (
                              <Line
                                key={dbKey}
                                type="monotone"
                                dataKey={dbKey}
                                stroke={color}
                                strokeOpacity={getOpacity(hoveredCommits, dbKey)}
                                strokeWidth={hoveredCommits === dbKey ? 2.5 : 1.5}
                                dot={getDot(hoveredCommits, dbKey, color)}
                              />
                            );
                          });
                      })()}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <span className="text-xs text-gray-500 italic">No commits data available</span>
                )}
              </div>
            </div>

            {/* Graph 2: Rollbacks */}
            <div className="">
              <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
                <div className="flex items-center gap-2 truncate">
                  <AlertTriangle className="w-4 h-4 text-rose-500 dark:text-red-400 shrink-0" />
                  <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider truncate">Rollbacks per Database</h4>
                </div>
                <ChartDbDropdown value={selectedRollbacksDb} onChange={setSelectedRollbacksDb} options={commitsData?.databases} />
              </div>
              <div className="h-40 w-full flex items-center justify-center">
                {hasData(rollbacksFormatted) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={rollbacksFormatted}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                      <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                      <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                      <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                      <Legend content={<ScrollableLegend hoveredSeries={hoveredRollbacks} onHoverSeries={setHoveredRollbacks} />} />
                      {(() => {
                        const allDbKeys = Object.keys(rollbacksFormatted[0] || {}).filter((k) => k !== 'time');
                        return allDbKeys
                          .filter((k) => selectedRollbacksDb === 'ALL' || k === selectedRollbacksDb)
                          .map((dbKey, i) => {
                            const origIndex = allDbKeys.indexOf(dbKey);
                            const colorIndex = origIndex >= 0 ? origIndex : i;
                            const color = colors[(colorIndex + 3) % colors.length];
                            return (
                              <Line
                                key={dbKey}
                                type="monotone"
                                dataKey={dbKey}
                                stroke={color}
                                strokeOpacity={getOpacity(hoveredRollbacks, dbKey)}
                                strokeWidth={hoveredRollbacks === dbKey ? 2.5 : 1.5}
                                dot={getDot(hoveredRollbacks, dbKey, color)}
                              />
                            );
                          });
                      })()}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <span className="text-xs text-gray-500 italic">No rollbacks data available</span>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: 2 Graphs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            {/* Graph 3: Active Sessions */}
            <div className="">
              <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
                <div className="flex items-center gap-2 truncate">
                  <RefreshCcw className="w-4 h-4 text-blue-500 dark:text-blue-400 shrink-0" />
                  <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider truncate">Sessions per DB</h4>
                </div>
                <ChartDbDropdown value={selectedConnsDb} onChange={setSelectedConnsDb} options={connectionsData?.databases} />
              </div>
              <div className="h-40 w-full flex items-center justify-center">
                {hasData(connectionsFormatted) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={connectionsFormatted}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                      <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                      <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                      <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                      <Legend content={<ScrollableLegend hoveredSeries={hoveredConns} onHoverSeries={setHoveredConns} />} />
                      {(() => {
                        const allDbKeys = Object.keys(connectionsFormatted[0] || {}).filter((k) => k !== 'time');
                        return allDbKeys
                          .filter((k) => selectedConnsDb === 'ALL' || k === selectedConnsDb)
                          .map((dbKey, i) => {
                            const origIndex = allDbKeys.indexOf(dbKey);
                            const colorIndex = origIndex >= 0 ? origIndex : i;
                            const color = colors[colorIndex % colors.length];
                            return (
                              <Line
                                key={dbKey}
                                type="monotone"
                                dataKey={dbKey}
                                stroke={color}
                                strokeOpacity={getOpacity(hoveredConns, dbKey)}
                                strokeWidth={hoveredConns === dbKey ? 2.5 : 1.5}
                                dot={getDot(hoveredConns, dbKey, color)}
                              />
                            );
                          });
                      })()}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <span className="text-xs text-gray-500 italic">No active session data available</span>
                )}
              </div>
            </div>

            {/* Graph 4: Rows Modified */}
            <div className="">
              <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
                <div className="flex items-center gap-2 truncate">
                  <Edit3 className="w-4 h-4 text-amber-500 dark:text-amber-400 shrink-0" />
                  <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider truncate">
                    {isMSSQL ? 'DML Activity' : 'Rows Modified'}
                  </h4>
                </div>
                <ChartDbDropdown value={selectedRowsDb} onChange={setSelectedRowsDb} options={rowsData?.databases} />
              </div>
              <div className="h-40 w-full flex items-center justify-center">
                {hasData(rowsFormatted) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={rowsFormatted}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                      <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                      <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                      <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                      <Legend content={<ScrollableLegend hoveredSeries={hoveredRows} onHoverSeries={setHoveredRows} />} />
                      <Line
                        type="monotone"
                        dataKey="Inserted"
                        stroke="#10b981"
                        strokeOpacity={getOpacity(hoveredRows, 'Inserted')}
                        strokeWidth={hoveredRows === 'Inserted' ? 2.5 : 1.5}
                        dot={getDot(hoveredRows, 'Inserted', '#10b981')}
                      />
                      <Line
                        type="monotone"
                        dataKey="Updated"
                        stroke="#f59e0b"
                        strokeOpacity={getOpacity(hoveredRows, 'Updated')}
                        strokeWidth={hoveredRows === 'Updated' ? 2.5 : 1.5}
                        dot={getDot(hoveredRows, 'Updated', '#f59e0b')}
                      />
                      <Line
                        type="monotone"
                        dataKey="Deleted"
                        stroke="#ef4444"
                        strokeOpacity={getOpacity(hoveredRows, 'Deleted')}
                        strokeWidth={hoveredRows === 'Deleted' ? 2.5 : 1.5}
                        dot={getDot(hoveredRows, 'Deleted', '#ef4444')}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <span className="text-xs text-gray-500 italic">No row modification data available</span>
                )}
              </div>
            </div>
          </div>

          {/* Row 3: 2 Graphs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            {/* Graph 5: Max Txn Time */}
            <div className="">
              <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
                <div className="flex items-center gap-2 truncate">
                  <Hourglass className="w-4 h-4 text-purple-500 dark:text-purple-400 shrink-0" />
                  <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider truncate">Max Txn Time by DB (sec)</h4>
                </div>
                <ChartDbDropdown value={selectedMaxTxnDb} onChange={setSelectedMaxTxnDb} options={txnsData?.databases} />
              </div>
              <div className="h-40 w-full flex items-center justify-center">
                {hasData(maxTxnFormatted) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={maxTxnFormatted}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                      <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                      <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                      <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                      <Legend content={<ScrollableLegend hoveredSeries={hoveredMaxTxn} onHoverSeries={setHoveredMaxTxn} />} />
                      {(() => {
                        const allDbKeys = Object.keys(maxTxnFormatted[0] || {}).filter((k) => k !== 'time');
                        return allDbKeys
                          .filter((k) => selectedMaxTxnDb === 'ALL' || k === selectedMaxTxnDb)
                          .map((dbKey, i) => {
                            const origIndex = allDbKeys.indexOf(dbKey);
                            const colorIndex = origIndex >= 0 ? origIndex : i;
                            const color = colors[(colorIndex + 2) % colors.length];
                            return (
                              <Line
                                key={dbKey}
                                type="monotone"
                                dataKey={dbKey}
                                stroke={color}
                                strokeOpacity={getOpacity(hoveredMaxTxn, dbKey)}
                                strokeWidth={hoveredMaxTxn === dbKey ? 2.5 : 1.5}
                                dot={getDot(hoveredMaxTxn, dbKey, color)}
                              />
                            );
                          });
                      })()}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <span className="text-xs text-gray-500 italic">No transaction time data available</span>
                )}
              </div>
            </div>

            {/* Graph 6: Open Transactions */}
            <div className="">
              <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
                <div className="flex items-center gap-2 truncate">
                  <FolderOpen className="w-4 h-4 text-blue-500 dark:text-blue-400 shrink-0" />
                  <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider truncate">Open Transactions by DB</h4>
                </div>
                <ChartDbDropdown value={selectedOpenTxnDb} onChange={setSelectedOpenTxnDb} options={txnsData?.databases} />
              </div>
              <div className="h-40 w-full flex items-center justify-center">
                {hasData(openTxnFormatted) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={openTxnFormatted}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                      <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                      <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                      <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                      <Legend content={<ScrollableLegend hoveredSeries={hoveredOpenTxn} onHoverSeries={setHoveredOpenTxn} />} />
                      {(() => {
                        const allDbKeys = Object.keys(openTxnFormatted[0] || {}).filter((k) => k !== 'time');
                        return allDbKeys
                          .filter((k) => selectedOpenTxnDb === 'ALL' || k === selectedOpenTxnDb)
                          .map((dbKey, i) => {
                            const origIndex = allDbKeys.indexOf(dbKey);
                            const colorIndex = origIndex >= 0 ? origIndex : i;
                            const color = colors[colorIndex % colors.length];
                            return (
                              <Line
                                key={dbKey}
                                type="monotone"
                                dataKey={dbKey}
                                stroke={color}
                                strokeOpacity={getOpacity(hoveredOpenTxn, dbKey)}
                                strokeWidth={hoveredOpenTxn === dbKey ? 2.5 : 1.5}
                                dot={getDot(hoveredOpenTxn, dbKey, color)}
                              />
                            );
                          });
                      })()}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <span className="text-xs text-gray-500 italic">No open transaction data available</span>
                )}
              </div>
            </div>
          </div>

          {/* Row 4: Query Throughput & Average Duration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            {/* Graph 7: Query Throughput */}
            <div>
              <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
                <Layers className="w-4 h-4 text-emerald-500 dark:text-green-400 shrink-0" />
                <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Query Throughput (by Type)
                </h4>
              </div>
              <div className="h-40 w-full flex items-center justify-center">
                {hasData(throughputFormatted) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={throughputFormatted}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                      <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                      <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                      <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                      <Legend content={<ScrollableLegend hoveredSeries={hoveredThroughput} onHoverSeries={setHoveredThroughput} />} />
                      {Object.keys(throughputFormatted[0] || {}).filter((k) => k !== 'time').map((key, i) => {
                        const color = colors[i % colors.length];
                        return (
                          <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={color}
                            strokeOpacity={getOpacity(hoveredThroughput, key)}
                            strokeWidth={hoveredThroughput === key ? 2.5 : 1.5}
                            dot={false}
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <span className="text-xs text-gray-500 italic">No query throughput data available</span>
                )}
              </div>
            </div>

            {/* Graph 8: Average Duration */}
            <div>
              <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
                <HardDrive className="w-4 h-4 text-amber-500 dark:text-amber-400 shrink-0" />
                <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Average Duration (by Type)
                </h4>
              </div>
              <div className="h-40 w-full flex items-center justify-center">
                {hasData(durationFormatted) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={durationFormatted}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                      <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                      <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                      <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                      <Legend content={<ScrollableLegend hoveredSeries={hoveredDuration} onHoverSeries={setHoveredDuration} />} />
                      {Object.keys(durationFormatted[0] || {}).filter((k) => k !== 'time').map((key, i) => {
                        const color = colors[(i + 2) % colors.length];
                        return (
                          <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={color}
                            strokeOpacity={getOpacity(hoveredDuration, key)}
                            strokeWidth={hoveredDuration === key ? 2.5 : 1.5}
                            dot={false}
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <span className="text-xs text-gray-500 italic">No query duration data available</span>
                )}
              </div>
            </div>
          </div>
        </div>)}


      {/* {!isES && (
        <div className="border-t border-gray-800 pt-4">
          <IndexesView
            activeCluster={activeCluster}
            timeRange={timeRange}
            clientId={clientId}
            refreshKey={refreshKey}
          />
        </div>
      )} */}

    </div>
  );
};
